import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, Star, ChevronLeft, ChevronRight, Ghost } from "lucide-react";
import { shortHash } from "@/lib/format";
import { addToWatchlist, isWatched, removeFromWatchlist } from "@/lib/watchlist";

export const Card = ({ children, className = "", ...props }) => (
  <div className={`rounded-xl border bg-card ${className}`} {...props}>{children}</div>
);

export const StatCard = ({ label, value, sub, icon: Icon, testId }) => (
  <Card className="p-4 sm:p-5 fade-up" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
      {Icon && <Icon size={13} className="text-primary" />} {label}
    </div>
    <p className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </Card>
);

export const CopyBtn = ({ text, testId = "copy-btn" }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-muted-foreground hover:text-primary transition-colors duration-150"
    >
      {copied ? <Check size={13} className="text-primary" /> : <Copy size={13} />}
    </button>
  );
};

export const WatchButton = ({ type, value, label }) => {
  const [watched, setWatched] = useState(isWatched(value));
  return (
    <button
      data-testid="watch-btn"
      onClick={() => {
        if (watched) { removeFromWatchlist(value); toast("Removed from watchlist"); }
        else { addToWatchlist({ type, value, label: label || shortHash(value) }); toast.success("Added to watchlist"); }
        setWatched(!watched);
      }}
      className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 transition-colors duration-200 ${
        watched ? "text-primary border-primary/40 bg-primary/10" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Star size={12} fill={watched ? "currentColor" : "none"} /> {watched ? "Watching" : "Watch"}
    </button>
  );
};

export const AddrLink = ({ hash, name, label, chars = 6, testId }) => {
  if (!hash) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/address/${hash}`} data-testid={testId} className="font-mono text-xs text-primary hover:underline" title={hash}>
      {label || name || shortHash(hash, chars)}
    </Link>
  );
};

export const TxLink = ({ hash, chars = 8 }) => (
  <Link to={`/tx/${hash}`} className="font-mono text-xs text-primary hover:underline" title={hash}>
    {shortHash(hash, chars)}
  </Link>
);

export const StatusBadge = ({ status }) => {
  const ok = status === "ok";
  const pending = status === null || status === undefined || status === "pending";
  return (
    <span data-testid="status-badge" className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${
      ok ? "bg-primary/10 text-primary" : pending ? "bg-yellow-500/10 text-yellow-500" : "bg-destructive/10 text-destructive"
    }`}>
      {ok ? "Success" : pending ? "Pending" : "Failed"}
    </span>
  );
};

export const TypeBadge = ({ children }) => (
  <span className="inline-flex text-[10px] rounded-full px-2 py-0.5 bg-secondary text-secondary-foreground whitespace-nowrap">{children}</span>
);

export const TokenLogo = ({ url, symbol, size = 24 }) => (
  url ? (
    <img src={url} alt={symbol} style={{ width: size, height: size }} className="rounded-full shrink-0"
      onError={(e) => { e.target.style.display = "none"; }} />
  ) : (
    <span style={{ width: size, height: size, fontSize: size * 0.4 }} className="rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold shrink-0">
      {(symbol || "?").slice(0, 2).toUpperCase()}
    </span>
  )
);

export const EmptyState = ({ title = "No recent activity found", subtitle, icon: Icon = Ghost }) => (
  <div data-testid="empty-state" className="flex flex-col items-center justify-center py-14 text-center">
    <Icon size={28} className="text-muted-foreground mb-3" />
    <p className="text-sm font-medium">{title}</p>
    {subtitle && <p className="text-xs text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
  </div>
);

export const SkeletonRows = ({ n = 6, h = "h-10" }) => (
  <div className="space-y-2 p-4" data-testid="loading-skeleton">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className={`${h} rounded-lg bg-muted animate-pulse`} />
    ))}
  </div>
);

export const ErrorState = ({ message = "Something went wrong loading data from Robinhood Chain. Please try again." }) => (
  <div data-testid="error-state" className="py-12 text-center">
    <p className="text-sm text-destructive">{message}</p>
  </div>
);

export const usePaged = (fetchPage, deps = []) => {
  const [items, setItems] = useState([]);
  const [nextParams, setNextParams] = useState(null);
  const [stack, setStack] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback((params, newStack) => {
    setLoading(true);
    setError(false);
    fetchPage(params || {})
      .then((data) => {
        setItems(data.items || []);
        setNextParams(data.next_page_params || null);
        setStack(newStack);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(null, []); }, [load]);

  const next = () => nextParams && load(nextParams, [...stack, nextParams]);
  const prevFixed = () => {
    const s = stack.slice(0, -1);
    load(s.length ? s[s.length - 1] : null, s);
  };
  return { items, loading, error, hasNext: !!nextParams, hasPrev: stack.length > 0, next, prev: prevFixed, page: stack.length + 1, refresh: () => load(stack.length ? stack[stack.length - 1] : null, stack) };
};

export const Pager = ({ pager }) => (
  <div className="flex items-center justify-end gap-2 p-3 border-t">
    <button data-testid="pager-prev" disabled={!pager.hasPrev} onClick={pager.prev}
      className="h-7 w-7 rounded-lg border flex items-center justify-center disabled:opacity-30 hover:bg-muted transition-colors duration-150">
      <ChevronLeft size={14} />
    </button>
    <span className="text-xs text-muted-foreground">Page {pager.page}</span>
    <button data-testid="pager-next" disabled={!pager.hasNext} onClick={pager.next}
      className="h-7 w-7 rounded-lg border flex items-center justify-center disabled:opacity-30 hover:bg-muted transition-colors duration-150">
      <ChevronRight size={14} />
    </button>
  </div>
);

export const DetailRow = ({ label, children, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-3 border-b last:border-0" data-testid={testId}>
    <span className="text-xs text-muted-foreground w-full sm:w-44 shrink-0 pt-0.5">{label}</span>
    <div className="text-sm min-w-0 flex-1 break-all">{children}</div>
  </div>
);
